// Catalog.ID Client — Agent API Keys Sub-Pane
// ============================================================================
// Lists API keys for an agent. Title row: create key. Revoking a key is a verb
// about ONE ROW, so it rides a button in that row — never the row click, which
// used to destroy the key the user merely wanted to read.

import { createListPaneType, createElement, renderPaneActions } from '@catalog-shell';
import type { ListPaneConfig, PaneTypeRenderContext } from '@catalog-shell';
import { DataSource } from '@catalog-shell/components';
import type { ColumnDef, FetchResult } from '@catalog-shell/components';
import * as agentApi from '../api/agent';
import type { ApiKeySummary } from '../api/types';
import { identity } from '../state/identity';
import { createCredentialDisplay } from '../components/credential-display';
import { createRowActionColumn, type RowActionSpec } from '@catalog-shell';
import { PANE_IDS } from '../shell-config';

const columns: ColumnDef<ApiKeySummary>[] = [
    {
        key: 'keyIdentifier',
        header: 'Key ID',
        width: 200,
        getValue: (item) => item.keyIdentifier,
        type: { kind: 'text' },
    },
    {
        key: 'scopes',
        header: 'Scopes',
        width: 160,
        getValue: (item) => item.scopes,
        type: { kind: 'text' },
    },
    {
        key: 'isRevoked',
        header: 'Status',
        width: 80,
        getValue: (item) => item.isRevoked ? 'Revoked' : 'Active',
        type: {
            kind: 'badge',
            backgroundColor: (v) => (v as string) === 'Active' ? 'rgba(80,200,80,0.3)' : 'rgba(220,50,50,0.3)',
        },
    },
    {
        key: 'creationTime',
        header: 'Created',
        width: 100,
        getValue: (item) => item.creationTime,
        type: {
            kind: 'text',
            converter: (v) => {
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
        },
    },
    {
        key: 'expiresAt',
        header: 'Expires',
        width: 100,
        getValue: (item) => item.expiresAt,
        type: {
            kind: 'text',
            converter: (v) => {
                if (!v) return 'Never';
                try { return new Date(v as string).toLocaleDateString(); }
                catch { return String(v); }
            },
            textColor: (v) => v ? undefined : '#555',
        },
    },
];

function createApiKeysDataSource(
    agentCid: string,
    remember: (items: ApiKeySummary[]) => void,
): DataSource<ApiKeySummary> {
    return new DataSource<ApiKeySummary>({
        fetch: async (params): Promise<FetchResult<ApiKeySummary>> => {
            const keys = await agentApi.listApiKeys(agentCid);
            const sliced = keys.slice(params.offset, params.offset + params.limit);
            remember(sliced);
            return { items: sliced, totalCount: keys.length };
        },
        keyFn: (item) => String(item.agentApiKeyId),
        pageSize: 100,
    });
}

/**
 * Pure: what a key's row offers. A revoked key is already dead — offering to
 * revoke it again would be a button that can only fail.
 */
export function planApiKeyRowAction(item: ApiKeySummary): RowActionSpec | null {
    if (item.isRevoked) return null;
    return { label: 'Revoke', variant: 'danger' };
}

/**
 * The column set is built once and shared by every instance of this pane type,
 * while the agent whose keys are listed (and the shell to talk to) belong to
 * the instance. The fetch that produced a row knows both, so it records them
 * against the row object itself — the only thing a cell click has in hand.
 */
const rowOrigin = new WeakMap<ApiKeySummary, { shell: PaneTypeRenderContext['shell']; agentCid: string }>();

const revokeColumn = createRowActionColumn<ApiKeySummary>({
    plan: planApiKeyRowAction,
    execute: (item) => {
        const origin = rowOrigin.get(item);
        if (!origin) return;
        void handleRevokeKey(origin.shell, item, origin.agentCid);
    },
});

export function createAgentApiKeysPane() {
    const config: ListPaneConfig<ApiKeySummary> = {
        typeId: PANE_IDS.agentApiKeys,
        defaultTitle: 'API Keys',
        createDataSource: (ctx) => {
            const agentCid = ctx.instance.context['agentCid'] as string;
            return createApiKeysDataSource(agentCid, (items) => {
                for (const item of items) rowOrigin.set(item, { shell: ctx.shell, agentCid });
            });
        },
        columns: [...columns, revokeColumn],
        keyFn: (item) => String(item.agentApiKeyId),
        initialMode: 'table',
        enabledModes: ['table'],
        selectionMode: 'none',
        onCreated: ({ dataSource }, ctx) => {
            ctx.disposables.add(ctx.shell.on('data-changed', (data) => {
                if ((data as { type?: string })?.type === 'agents') {
                    dataSource.refresh();
                }
            }));
        },
        // No onItemSelect: a click on a row selects it and nothing more. The
        // row's verb is the Revoke button in the row itself.
        // The list's own verb belongs in its title row, not in a body toolbar
        // (shell USAGE.md "Where a button belongs").
        tabBarActions: (ctx, dataSource) => renderPaneActions([
            {
                key: 'create',
                label: '+ Create Key',
                variant: 'primary',
                execute: () => void handleCreateKey(
                    ctx.shell, ctx.instance.context['agentCid'] as string, dataSource),
            },
        ]),
        ariaLabel: 'Agent API keys',
    };
    return createListPaneType(config);
}

async function handleCreateKey(shell: PaneTypeRenderContext['shell'], agentCid: string, ds: DataSource<ApiKeySummary>): Promise<void> {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Create API Key' }));

    content.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Select the scopes this API key may use. Only the selected operations will be permitted.',
    }));

    const canManageOrgKeys = identity.getActive()?.accountType === 'delegate'
        && (identity.hasRole('owner') || identity.hasRole('admin'));
    const availableScopes = agentApi.filterAgentScopes(canManageOrgKeys);

    const selectedScopes = new Set<string>();
    const scopeGroup = createElement('div', { className: 'cid-scope-list' });
    for (const scope of availableScopes) {
        const row = createElement('label', { className: 'cid-auth-checkbox-row' });
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.id = `apikey-scope-${scope.id}`;
        cb.addEventListener('change', () => {
            if (cb.checked) selectedScopes.add(scope.id);
            else selectedScopes.delete(scope.id);
        });
        row.appendChild(cb);
        const text = createElement('div', { className: 'cid-scope-text' });
        text.appendChild(createElement('div', { className: 'cid-scope-label', textContent: scope.label }));
        text.appendChild(createElement('div', { className: 'cid-scope-description', textContent: scope.description }));
        row.appendChild(text);
        scopeGroup.appendChild(row);
    }
    content.appendChild(scopeGroup);

    // Expiration selector — preset dropdown with a "Custom date" escape hatch.
    // Server accepts `expiresAt: DateTime?` at CreateApiKeyRequest (AgentController.cs:436).
    const expiryField = createElement('div', { className: 'cid-apikey-expiry' });
    expiryField.appendChild(createElement('label', {
        className: 'cid-apikey-expiry-label',
        textContent: 'Expiration',
    }));
    const expirySelect = document.createElement('select');
    expirySelect.className = 'cid-apikey-expiry-select';
    const expiryPresets: { value: string; label: string; days?: number }[] = [
        { value: 'never', label: 'Never expires' },
        { value: '30',    label: '30 days',  days: 30 },
        { value: '60',    label: '60 days',  days: 60 },
        { value: '90',    label: '90 days',  days: 90 },
        { value: '365',   label: '1 year',   days: 365 },
        { value: 'custom', label: 'Custom date…' },
    ];
    for (const p of expiryPresets) {
        const opt = document.createElement('option');
        opt.value = p.value;
        opt.textContent = p.label;
        expirySelect.appendChild(opt);
    }
    expiryField.appendChild(expirySelect);

    const customDateInput = document.createElement('input');
    customDateInput.type = 'date';
    customDateInput.className = 'cid-apikey-expiry-date';
    customDateInput.style.display = 'none';
    // Default custom date = 30 days out, as a sensible starting point.
    const defaultCustom = new Date(Date.now() + 30 * 86400_000);
    customDateInput.value = defaultCustom.toISOString().slice(0, 10);
    // Prevent picking a past date.
    customDateInput.min = new Date().toISOString().slice(0, 10);
    expiryField.appendChild(customDateInput);

    expirySelect.addEventListener('change', () => {
        customDateInput.style.display = expirySelect.value === 'custom' ? 'block' : 'none';
    });
    content.appendChild(expiryField);

    const submitBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Create Key' });
    content.appendChild(submitBtn);

    const modal = shell.showModal(content);

    submitBtn.addEventListener('click', async () => {
        if (selectedScopes.size === 0) { shell.toast('Select at least one scope', 'warning'); return; }

        // Compute expiresAt from the selected preset or custom date.
        let expiresAt: string | undefined;
        const preset = expiryPresets.find(p => p.value === expirySelect.value);
        if (preset?.days !== undefined) {
            expiresAt = new Date(Date.now() + preset.days * 86400_000).toISOString();
        } else if (expirySelect.value === 'custom') {
            if (!customDateInput.value) {
                shell.toast('Pick an expiration date', 'warning');
                return;
            }
            // Interpret as end-of-day UTC on the selected date.
            expiresAt = new Date(`${customDateInput.value}T23:59:59Z`).toISOString();
        }
        // 'never' → expiresAt stays undefined (server stores NULL)

        submitBtn.setAttribute('disabled', '');
        submitBtn.textContent = 'Creating...';

        try {
            const resp = await agentApi.createApiKey(agentCid, {
                scopes: Array.from(selectedScopes),
                expiresAt,
            });

            // Emit data-changed immediately so the list refreshes even if the user
            // dismisses the modal via overlay click instead of pressing Done.
            shell.emit('data-changed', { type: 'agents' });

            content.innerHTML = '';
            content.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'API Key Created' }));
            content.appendChild(createElement('p', {
                className: 'cid-wizard-warning',
                textContent: 'This secret will not be shown again. Save it now.',
            }));

            const expiresLabel = expiresAt
                ? new Date(expiresAt).toLocaleString()
                : 'Never';

            const credDisplay = createCredentialDisplay({
                fields: [
                    { label: 'Agent CID', value: agentCid },
                    { label: 'Key Identifier', value: resp.keyIdentifier },
                    { label: 'Secret', value: resp.secret },
                    { label: 'Scopes', value: Array.from(selectedScopes).join(', '), monospace: false },
                    { label: 'Expires', value: expiresLabel, monospace: false },
                ],
                downloadFilename: `catalog-apikey-${agentCid}-${resp.keyIdentifier.slice(-8)}.txt`,
                downloadFooter: 'Keep this file in a secure location. If the secret is lost or compromised, revoke this key and create a new one.',
            });
            content.appendChild(credDisplay);

            const doneBtn = createElement('button', { className: 'cs-btn cs-btn--yes', textContent: 'Done' });
            doneBtn.addEventListener('click', () => { modal.close(); ds.refresh(); });
            content.appendChild(doneBtn);
        } catch (err) {
            submitBtn.removeAttribute('disabled');
            submitBtn.textContent = 'Create Key';
            shell.toast(err instanceof Error ? err.message : 'Failed to create key', 'error');
        }
    });
}

async function handleRevokeKey(
    shell: PaneTypeRenderContext['shell'],
    item: ApiKeySummary,
    agentCid: string,
): Promise<void> {
    const ok = await shell.confirm({
        title: 'Revoke API Key',
        message: `Revoke key ${item.keyIdentifier}? This cannot be undone.`,
        confirmLabel: 'Revoke',
        confirmVariant: 'no',
    });
    if (!ok) return;
    try {
        await agentApi.revokeApiKey(agentCid, item.agentApiKeyId);
        shell.emit('data-changed', { type: 'agents' });
        shell.toast('API key revoked', 'success');
    } catch (err) {
        shell.toast(err instanceof Error ? err.message : 'Failed to revoke key', 'error');
    }
}
